import {React, _, StyledProps, classNames, observer, utils, useEffect, Fragment} from '@api'
import {HTMLDivProps, Icon} from '@blueprintjs/core'
import {Popover2} from '@api'
import {Typography, Tooltip} from '@material-ui/core'
import {BuilderCardFieldStore, BuilderCardLoader, BuilderCardStore, NestedItemFieldToolbarButtons, useBuilderCardStore, useCardFieldStore, useDataGridStore} from '@modules/catalog'
import {useVipLocalStore, VipLocalStore} from '@modules/VipLocalStore'
import {DragDropEventHandlers, ICatalogFieldConfig, StoresInstance, ui, useCatalog} from '@stores'
import {action, computed, observable} from 'mobx'
import {useAsObservableSource, useLocalStore} from 'mobx-react'
import {Modifiers as PopperModifiers} from 'popper.js'
import {useAsync} from 'react-use'
import {isNullOrUndefined} from 'util'
import * as css from './SpecificationItem.css'
import {Link} from '@material-ui/icons'
import {CollapsibleStateful, PrimaryIconButton} from 'centric-ui-components'

interface MyProps extends StyledProps {
  title?: React.ReactNode
  valueComponent?: React.ReactNode,
  link?: boolean
  showTitle?: boolean
  info?: any
  library?: string
  className?: string
  popperProps?: any
  valueTitle?: string
  titledField?: boolean
  placeholderText?: string
  field?: ICatalogFieldConfig
  draggable?: boolean
  onDragStart?: (e: React.DragEvent) => void
  noPopover?: boolean
  editable?: boolean
  ref?: any
  isPopover?: boolean
}

export const WINDOW_OVERFLOW_POPOVER_MODIFIERS: PopperModifiers = ({
  flip: {
    enabled: true,
    boundariesElement: 'window'
  },
  offset: {
    offset: '0,2'
  },
  preventOverflow: {
    enabled: true,
    boundariesElement: 'window'
  }
})

const IMAGES_FIELD_ARRAY = ['images']

export const SpecificationItemStoreContext = React.createContext<SpecificationItemStore>(null)
export const useSpecificationItemStore = () => React.useContext(SpecificationItemStoreContext)

export class SpecificationItemStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Pick<MyProps, 'noPopover' | 'valueComponent' | 'info' | 'link' | 'field' | 'showTitle' | 'title' | 'valueTitle' | 'titledField' | 'draggable' | 'onDragStart' | 'isPopover'>, public cardStore: BuilderCardStore, public fieldStore: BuilderCardFieldStore) {
    super(stores)

    const fieldIndex = props.field.esIndex,
      {catalog} = this
    this.isRefTypeDroppableField = _.some(catalog.catalogConfig.fields, f => f.esIndex === fieldIndex && !_.isEmpty(f.refField) && f.field !== props.field.field)
    this.dragDropEvents = this.isRefTypeDroppableField && new DragDropEventHandlers(catalog, {target: fieldIndex, id: cardStore.data.id})
  }

  isRefTypeDroppableField: boolean
  dragDropEvents: DragDropEventHandlers

  @action onToggleOpen = () => {
    const {collapsed, catalog: {settings: {cardView}}, props: {field}} = this
    cardView[field.field] = {...(cardView[field.field] ?? {}), collapsed: !collapsed}
  }

  @observable dragOver = false

  onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    this.isRefTypeDroppableField && (this.dragDrop.dragType === 'Nested Item') && (this.dragOver = true)
  }

  onDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    this.dragOver = false
  }

  onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (this.isRefTypeDroppableField && this.dragDrop.dragType === 'Nested Item') {
      // this.cardStore.stopDraggingItem()
      this.fieldStore.rendererStore.onChangeSpec({..._.omit(this.dragDrop?.dragItem?.targetExtra?.data, ['__parent', '__path'])}, this.props.field.field)
      // this.dragDropEvents?.events?.onDrop(e)
      this.dragOver = false
    }
    this.fieldStore.dragHandlers.onDrop(e)
  }

  @computed get showTitle() {
    return this.props.showTitle || this.collapsed
  }

  @observable.ref nestedValue: any

  @computed get tooltipTitle() {
    const {nestedValue, props: {field, title, valueTitle}} = this
    return nestedValue || field?.renderer === 'html' ? '' : _.isString(title) && _.isString(valueTitle) ? `${title}: ${valueTitle}` : _.isString(title) ? title : ''
  }

  @computed get collapsed() {
    const {props: {field}, catalog} = this
    return catalog.settings.cardView[field.field]?.collapsed ?? false
  }

  @computed get imageSourceFields() {
    const {props: {field}, catalog} = this
    return (field?.esIndex == catalog.index ? catalog.selectedImageSourceFields : _.first(catalog.workspaceByIndex[field.esIndex]?.catalog?.imageSources)?.fields) ?? IMAGES_FIELD_ARRAY
  }

  get valueComponent() { return this.props.valueComponent}

  /**
   * Might be raw psuedo-html, might be a string, might be a {@see SidebarSpecification}, might be '--' (no value), etc
   */
  @computed get children() {
    const {strings, valueComponent, stores} = this
    return React.isValidElement(valueComponent)
           ? valueComponent : !_.isNumber(valueComponent) && _.isEmpty(valueComponent) ? isNullOrUndefined(valueComponent) ? strings.emptyLabel : (stores.ui?.settings?.showNoValueAsEmpty ? strings.emptyLabel : strings.noValue)
                                                                                       : _.isString(valueComponent) ? /*<Highlighter textToHighlight={value} searchWords={searchWords}/>*/ valueComponent : valueComponent
  }

  @computed get nestedInfo() {
    const {nestedValue, props: {info}} = this
    let nestedInfo: any = nestedValue ?? info

    if (_.isArray(nestedInfo)) {
      nestedInfo = _.first(nestedInfo)
    }// Happens with text renderers of refLists - see YFTS-9874

    return nestedInfo
  }

  @computed get id() {
    const {nestedInfo, catalog, fieldStore, cardStore, props: {field}} = this

    return catalog.idFor(nestedInfo ?? (field?.esIndex ? fieldStore?.value?.val : null))
  }

  @computed get allowHoverPopover() {
    const {props: {field, noPopover}, props, id, cardStore: {isCardFieldDragging}} = this
    return !_.isArray(props.info) && field?.esIndex != null && id != null && !noPopover && !isCardFieldDragging
  }
}

/**
 * A single field of a catalog item
 * Can be a string, a dropdown, a list of chips (tags), a numeric control, a button to add a new linked item like color or material, a rich 'inner' card with complex content {@see SidebarSpecificationCard},
 * an array of smaller 'chip' cards, etc
 */
export const SpecificationItem = observer<Omit<HTMLDivProps, 'title'> & MyProps, HTMLDivElement>(function SpecificationItem({
  link,
  valueComponent,
  info,
  className,
  popperProps,
  showTitle,
  style,
  title,
  valueTitle,
  field,
  titledField,
  placeholderText,
  draggable,
  onDragStart,
  noPopover,
  editable,
  isPopover,
  ...props
}, ref) {
  const dataGridStore = useDataGridStore()

  showTitle = showTitle && !dataGridStore
  //console.log(`render ${title}`)
  const obsProps = useAsObservableSource({field, titledField, showTitle, link, draggable, onDragStart, title, valueTitle, valueComponent, isPopover}),
    cardStore = useBuilderCardStore(), fieldStore = useCardFieldStore(),
    store = useVipLocalStore((ws) => new SpecificationItemStore(ws, obsProps, cardStore, fieldStore)),
    {children, collapsed, tooltipTitle, es, dragDropEvents, onDragOver, onDragLeave, onDrop, dragOver, onToggleOpen} = store,
    catalog = useCatalog()

  showTitle = store.showTitle

  useEffect(() => {
    return () => {
      if (catalog.currentClickPopoverBuilderCardField == fieldStore) {
        catalog.currentClickPopoverBuilderCardField = null
      }
    }
  }, [fieldStore])

  const {value: nestedValue} = useAsync(async () => {
    // console.log({index: field?.esIndex, info})
    if (field?.esIndex && _.isString(info)) {
      try {
        return await es.get({index: field.esIndex, id: info}, {log: {query: false}})
      } catch (err) {
        return `${catalog.labelForLibrary({id: catalog.libraryByIndex[field.esIndex].id})} is missing`
      }
    }
    return null
  }, [])

  useEffect(() => {
    store.nestedValue = nestedValue
  }, [nestedValue])

  const {id, allowHoverPopover, nestedInfo} = store

  // Unused
    // imageUrl = React.useMemo(() => {
    //   if (!nestedInfo || _.isString(nestedInfo)) return null
    //   else if (nestedInfo.hex) return nestedInfo.hex
    //   else {
    //     const images = _.flatMap(imageSourceFields.map(f => _.get(nestedInfo, f))).filter(_.identity)
    //     return catalog.imageUrlFor(_.first(images) || _.first(nestedInfo))
    //   }
    // }, [nestedInfo, imageSourceFields]),

    /**
     * Hover popover for a given field in a catalog card
     * Hover = existing information about a linked item (a color, a material, etc)
     * @see {SidebarSpecificationCard, SidebarSpecification}
     * @param children
     */
  const maybeRenderPopover = children => {
      return !allowHoverPopover
             ? children
             : <Popover2
               interactionKind='hover'
               position='right'
               hoverOpenDelay={ui.settings.cardPopover.ms}
               hoverCloseDelay={ui.settings.cardPopover.closeMs}
               popoverClassName={css.popover}
               portalClassName={css.popoverPortal}
               disabled={fieldStore?.specPopoverOpen || catalog.currentClickPopoverBuilderCardField != null}
               lazy
               usePortal
               transitionDuration={ui.settings.cardPopover.ms}
               autoFocus={false}
               captureDismiss={false}
               enforceFocus={false}
               //modifiers={{popperOffsets: {}} //fieldStore?.cardStore?.props?.isPopover !== true}}}
               //modifiers={WINDOW_OVERFLOW_POPOVER_MODIFIERS}
               content={<BuilderCardLoader
                 hideButtons
                 isPopover
                 levelOfDetail={1}
                 nested={{field, isInCardField: true, data: nestedInfo, parentData: store.cardStore.data}}
                 field={field}
                 id={id}
               />
               }>
               {children}
             </Popover2>
    }

  //field?.esIndex && console.log(`Render hover card for builder card field spec item?`, {showPopover, title, field : field.field, index: field.esIndex, children, nestedInfo: toJS(nestedInfo)})

  // console.log({field, nestedValue, info, noPopover})

  const isDragging = cardStore?.isCardFieldDragging
  return (
    <SpecificationItemStoreContext.Provider value={store}>
      <div {..._.omit(props, 'title')}
           className={classNames(css.root, className)}
           ref={ref}
           data-type='SpecificationItem'
           data-show-title={showTitle}
           data-draggable={draggable}
           data-editable={editable}
           data-is-dragging={isDragging}
           data-children-count={fieldStore?.value?.val?.length ?? 1} // Something like field: 'articles' value: { val: [{id: 'style1'}, {id: 'style12'}]
           {..._.omit(dragDropEvents?.events, ['onDragEnter', 'onDragLeave', 'onDrop'])}
           onDragOver={onDragOver}
           onDragLeave={onDragLeave}
           onDrop={onDrop}
           style={style}>
        <div className={classNames(css.details, {[css.dragOver]: dragOver})}
             data-type='SpecificationItemDetails'>
          {showTitle ? <CollapsibleStateful open={!collapsed}
                                            useMaterialCollapse={false}
                                            className={css.collapsible}
                                            onToggleOpen={onToggleOpen}
                                            hideExpandCollapseButton
                                            title={<SpecificationItemTitleBar/>}>
                       <SpecificationItemContents maybeRenderPopover={maybeRenderPopover} children={children}/>
                     </CollapsibleStateful>
                     : <Fragment>
             <div data-tooltip-wrapper title={dataGridStore || isPopover || allowHoverPopover ? '' : (title ?? tooltipTitle) as any}>
               {<SpecificationItemContents maybeRenderPopover={maybeRenderPopover} children={children}/>}
             </div>
           </Fragment>}
        </div>
      </div>
    </SpecificationItemStoreContext.Provider>
  )
}, {forwardRef: true})

const SpecificationItemTitleBar = observer(function TitleBar() {
  const {props: {title, titledField, draggable, onDragStart, isPopover}, showTitle, tooltipTitle, fieldStore: {field, field: {card, renderer}, cardStore: {onDrillDown}, value, data, nestedItemsAsList, toggleNestedItemsAsList}} = useSpecificationItemStore(),
    dataGridStore = useDataGridStore()

  if (dataGridStore) return null

  return (<Fragment>
    <div className={css.titleBar} data-type='TitleBar'>
      {/*<Icon className={css.isDraggingIcon} icon='property' />*/}

      {showTitle &&
       (titledField ? <Typography title={tooltipTitle} variant={'body1'} data-type='title' className={css.title}>
                      {React.isValidElement(title) ? title : title}
                    </Typography> : //<Highlighter textToHighlight={title} searchWords={searchWords}/>
        <Typography title={tooltipTitle} variant={'body1'} data-type='title' className={css.title}>
          {title}
        </Typography>)}
    </div>
    {!isPopover && <div className={css.toolbarButtons}>
      {card?.nested?.drilldown &&
	   <PrimaryIconButton onClick={onDrillDown({field, items: value?.val, parent: data})}
	                      disabled={_.isEmpty(value?.val)}
	                      tooltip={'Drill Down'}
	                      wrapperClassName={css.drilldownButton}
	                      icon={<Icon icon='arrow-right'/>}/>
      }
      {card?.nested && Array.isArray(value.val) && renderer != 'text' ? <Fragment>
        <PrimaryIconButton onClick={toggleNestedItemsAsList} wrapperClassName={css.toggleNestedWrapper}
                           className={css.toggleNestedItems}
                           tooltip={nestedItemsAsList ? 'Show as icons' : 'Show as list'}
                           icon={<Icon icon={nestedItemsAsList ? 'menu' : 'more'}/>}/>
      </Fragment> : null}
      {showTitle && <ToggleExpandCollapseCardFieldToolbarButton className={css.toggleCollapseField}/>}
		<NestedItemFieldToolbarButtons/>
		<RemoveCardFieldToolbarButton className={css.removeCardField}/>
      {draggable &&
	   <div className={css.dragHandle}
	        title={'Reorder Fields'}
	        draggable
	        onDragStart={e => {
              onDragStart && onDragStart(e)
              //utils.eatMouseEvent(e)
            }}
	        onClick={utils.eatMouseEvent}
	   >
		   <Icon icon={'drag-handle-horizontal'}/>
	   </div>}
	</div>}
  </Fragment>)
})

const SpecificationItemContents = observer<{ maybeRenderPopover?, children? }, any>(function SpecificationItemContents({maybeRenderPopover, children}, ref) {
  const {props: {link}, showTitle, fieldStore} = useSpecificationItemStore()

  return (
    <div ref={ref} className={css.valueContainer}
         data-type='valueContainer'
         data-renderer={fieldStore?.rendererStore?.renderer}
         data-component={fieldStore?.valueComponent?.type?.displayName}
    >
      <div className={css.valueContent}>
        {/*editable && (field.def?.type === 'Enum' || field.def?.type === 'EnumList') ?
                    <XRefSidebarField align={align} field={field} className={css.xrefField}/>
                    :*/
          maybeRenderPopover(
            <div className={css.valueInfo}>
              {/*{Array.isArray(nestedInfo) ? null : imageUrl && !editable ? // If editable this will be rendered by a sidebar specification component that will draw the image itself*/}
              {/*                                    (imageUrl?.startsWith('#')*/}
              {/*                                     ? <div className={css.hex} style={{backgroundColor: imageUrl}}/>*/}
              {/*                                     : <AwesomeQrCode*/}
              {/*                                       className={classNames(css.image)}*/}
              {/*                                       imageUrl={imageUrl}*/}
              {/*                                     />)*/}
              {/*  //: nestedInfo ? <div className={css.placeholder}><span>{placeholderText ?? (title && _.isString(title) ? title[0] : 'u')}</span></div>*/}
              {/*                                                          : null}*/}
              <span draggable
                    onDragStart={utils.eatMouseEvent}
                    onDoubleClick={utils.eatMouseEvent}
                // onClick={utils.eatMouseEvent}
                    className={css.value} data-type='SpecificationItemValue'>
                    {React.isValidElement(children)
                     ? children
                     : <span className={css.htmlTextContent} dangerouslySetInnerHTML={{__html: children}}/>}</span>
            </div>)}

        {link && <Link className={css.link}/>}

        {!showTitle && <SpecificationItemTitleBar/>}
      </div>
    </div>)
}, {forwardRef: true})

const RemoveCardFieldToolbarButton = observer(function RemoveCardFieldToolbarButton({className}: { className?: string }) {
  const {field: {field}, catalog} = useCardFieldStore()

  return (<div className={classNames(className)} data-field={field}>
    <PrimaryIconButton icon={'cross'} tooltip={'Remove Field'} onClick={() => catalog.cv.cardView_toggleOff(field)}/>
  </div>)
})
const ToggleExpandCollapseCardFieldToolbarButton = observer(function ToggleExpandCollapseSpecItemToolbarButton({className}: { className?: string }) {
  const {collapsed, onToggleOpen} = useSpecificationItemStore()

  return (<div className={classNames(css.toggleExpandCollapse, className)} data-collapsed={collapsed === true} data-type='toggle-expand-collapse-field'>
    <PrimaryIconButton tooltip={collapsed === true ? 'Expand Field' : 'Collapse Field'} icon={'chevron-down'} onClick={onToggleOpen}/>

  </div>)
})


// WEBPACK FOOTER //
// ./src/modules/catalog/product/productCard/components/BuilderCard/SpecificationItem.tsx